// Libs
import { Document, Image, Page, Text, View, Link } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import {
  formatDate,
  getFullName,
  convertTimeToHHmm,
  formatCurrencyUSD,
  formatPhoneNumber,
} from '~/utils/helper/common';
import { IOrderPdf } from '~/utils/interface/order';
import { DateFormatEnum, TypeListInventory } from '~/utils/enum/common';
import { EMPTY_STRING, PDF_POSITION_PLACEHOLDER_TO_SIGNING } from '~/utils/constants/common';
// Styles, images, icons
import { styles } from './styles';
import { images } from '~/assets';

type Props = { orderPdf: IOrderPdf };

const OrderPdf = (props: Props) => {
  //#region Destructuring Props
  const { orderPdf } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.mainContainer}>
          <View style={styles.header}>
            <View style={styles.viewOneHeader}>
              <Image style={styles.logo} src={images.commonLogo} />

              <View style={styles.viewRightOneHeader}>
                <Text style={[styles.titleTitle, styles.viewLeft]}>{t('order_pdf_title')}</Text>

                <Text style={[styles.textLabel, styles.viewLeft]}>{t('order_pdf_sub_title')}</Text>

                <Text style={[styles.textContent, styles.viewLeft]}>{t('order_pdf_address')}</Text>
              </View>
            </View>

            <View style={styles.viewLine} />

            <View style={styles.viewTwoHeader}>
              <View style={styles.viewLeftTwoHeader}>
                <Text style={styles.textSubTitle}>{t('order_pdf_bill_to')}</Text>
                <Text style={styles.textLabel}>
                  {getFullName({ firstName: orderPdf.client.firstName, lastName: orderPdf.client.lastName })}
                </Text>
                <View style={styled.height(6)} />
                <Text style={styles.textContent}>
                  {orderPdf?.client?.clientContacts && orderPdf?.client?.clientContacts[0]?.phoneNumber
                    ? formatPhoneNumber(orderPdf?.client?.clientContacts[0]?.phoneNumber)
                    : EMPTY_STRING}
                </Text>
                <Text style={styles.textContent}>
                  {orderPdf?.client?.clientContacts && orderPdf?.client?.clientContacts[0]?.email
                    ? orderPdf?.client?.clientContacts[0]?.email
                    : EMPTY_STRING}
                </Text>
              </View>

              <View style={styles.viewRightTwoHeader}>
                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_order_number')}</Text>
                  <Text style={styles.textContent}>{orderPdf?.orderNumber}</Text>
                </View>

                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_po_number')}</Text>
                  <Text style={styles.textContent}>{orderPdf?.poNumber}</Text>
                </View>

                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_event_start')}</Text>
                  <Text style={styles.textContent}>
                    {`${formatDate(orderPdf?.startDate, DateFormatEnum.MM_DD_YYYY)} - ${convertTimeToHHmm(
                      orderPdf?.startTime
                    )}`}
                  </Text>
                </View>

                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_event_end')}</Text>
                  <Text style={styles.textContent}>
                    {`${formatDate(orderPdf?.endDate, DateFormatEnum.MM_DD_YYYY)} - ${convertTimeToHHmm(
                      orderPdf?.endTime
                    )}`}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.tableContainer}>
            <Text style={styles.textSubTitle}>{t('order_pdf_product')}</Text>

            <View style={styles.tableHeader}>
              <Text style={styles.tableHeaderCells}>{t('order_pdf_name')}</Text>
              <Text style={styles.tableHeaderCellType}>{t('order_pdf_type')}</Text>
              <Text style={styles.tableHeaderCellType}>{t('order_pdf_quantity')}</Text>
              <Text style={styles.tableHeaderCell}>{t('order_pdf_price')}</Text>
              <Text style={styles.tableHeaderCell}>{t('order_pdf_total')}</Text>
            </View>
            {orderPdf?.dataProduct &&
              orderPdf?.dataProduct.length > 0 &&
              orderPdf?.dataProduct
                ?.filter((item) => !item.isShowOnPrint)
                ?.map((item) => (
                  <View key={item.id} style={styles.tableRow}>
                    <View style={styles.tableCellName}>
                      {(() => {
                        return item?.url ? (
                          <Link src={item?.url} style={styles.textLink}>
                            <Text>{item?.name}</Text>
                          </Link>
                        ) : (
                          <Text>{item?.name}</Text>
                        );
                      })()}
                    </View>
                    <Text style={styles.tableCellType}>
                      {item?.parentType
                        ? item?.type === TypeListInventory.PRODUCT && item?.parentType === TypeListInventory.PACKAGE
                          ? t('order_pdf_package_item')
                          : t('order_pdf_add_on')
                        : item?.type === TypeListInventory.PACKAGE
                        ? t('order_pdf_package')
                        : t('order_pdf_product')}
                    </Text>
                    <Text style={styles.tableCellType}>{item?.quantity}</Text>
                    <Text style={styles.tableCell}>{formatCurrencyUSD({ value: item?.price, showDecimal: true })}</Text>
                    <Text style={styles.tableCell}>{formatCurrencyUSD({ value: item?.total, showDecimal: true })}</Text>
                  </View>
                ))}
          </View>

          <View style={styles.viewNoteContent}>
            <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_external_note')}</Text>
            <Text>{orderPdf?.externalNote}</Text>
          </View>

          <View style={styles.viewTotalContainer}>
            <View style={styles.viewAdjustment}>
              <Text style={styles.textSubTitle}>{t('order_pdf_additional_charges')}</Text>

              <View style={styles.viewAdjustmentContainer}>
                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_delivery_cost')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.deliveryCost, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_damage_waiver')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.damageWaiver, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_labor_fee')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.laborFee, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_discount')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.discountRate, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_deposit')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.depositMadeAmount, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_coupon')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.couponAmount, showDecimal: true })}</Text>
                </View>

                {orderPdf?.customFees &&
                  orderPdf?.customFees?.length > 0 &&
                  orderPdf?.customFees?.map((item) => (
                    <View style={styles.viewAdjustmentItem}>
                      <Text style={styles.viewAdjustmentLabel}>{item.customFeeName}</Text>
                      <Text>{formatCurrencyUSD({ value: item.customFeeAmount, showDecimal: true })}</Text>
                    </View>
                  ))}
              </View>
            </View>

            <View style={styles.viewTotalContent}>
              <Text style={styles.textSubTitle}>{t('order_pdf_total')}</Text>
              <View style={styles.viewAdjustmentContainer}>
                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_subtotal')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.productSubtotal, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_tax')}</Text>
                  <Text>{formatCurrencyUSD({ value: orderPdf?.taxAmount, showDecimal: true })}</Text>
                </View>

                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_total_title')}</Text>
                  <Text style={styles.viewAdjustmentLabel}>
                    {formatCurrencyUSD({ value: orderPdf?.total, showDecimal: true })}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.viewSignature}>
            <View style={styles.viewSignatureClient}>
              <Text style={styles.textSubTitle}>{t('order_pdf_client_signature')}</Text>
              <Text style={{ fontSize: 40, opacity: 0 }}>{PDF_POSITION_PLACEHOLDER_TO_SIGNING}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styled = {
  height: (height: number) => ({
    height: height,
  }),
};

export default OrderPdf;
